/**
 * Student-controlled pacing timer: preference + per-attempt storage and clock
 * math. Purely client-side and best-effort: if storage is blocked (e.g. a
 * partitioned iframe), reads fall back to defaults and writes are dropped.
 */
import { z } from "zod"

export const PER_QUESTION_PRESETS_SECONDS = [
  30, 45, 60, 90, 120, 180, 300,
] as const
export const WHOLE_TEST_PRESETS_MINUTES = [
  10, 15, 20, 30, 45, 60, 90, 120, 180, 240,
] as const

const modeSchema = z.enum(["off", "per_question", "whole_test"])
export type StudentTimerMode = z.infer<typeof modeSchema>

export interface StudentTimerPreference {
  mode: StudentTimerMode
  perQuestionSeconds: number
  wholeTestMinutes: number
  sound: boolean
}

export const DEFAULT_STUDENT_TIMER_PREFERENCE: StudentTimerPreference = {
  mode: "off",
  perQuestionSeconds: 60,
  wholeTestMinutes: 30,
  sound: false,
}

const presetSchema = (presets: readonly number[], fallback: number) =>
  z
    .number()
    .refine((value) => presets.includes(value))
    .catch(fallback)

const preferenceSchema = z.object({
  mode: modeSchema.catch(DEFAULT_STUDENT_TIMER_PREFERENCE.mode),
  perQuestionSeconds: presetSchema(
    PER_QUESTION_PRESETS_SECONDS,
    DEFAULT_STUDENT_TIMER_PREFERENCE.perQuestionSeconds,
  ),
  wholeTestMinutes: presetSchema(
    WHOLE_TEST_PRESETS_MINUTES,
    DEFAULT_STUDENT_TIMER_PREFERENCE.wholeTestMinutes,
  ),
  sound: z.boolean().catch(DEFAULT_STUDENT_TIMER_PREFERENCE.sound),
})

const seconds = z.number().finite().nonnegative()

const questionOvertimeEntrySchema = z.object({
  limitSeconds: seconds,
  overtimeSeconds: seconds,
})
export type QuestionOvertimeEntry = z.infer<typeof questionOvertimeEntrySchema>

const attemptRecordSchema = z.object({
  wholeTest: z
    .object({
      startedAt: seconds,
      limitSeconds: seconds,
      elapsedSeconds: seconds.optional(),
    })
    .optional()
    .catch(undefined),
  questionOvertime: z.record(questionOvertimeEntrySchema).catch({}),
  updatedAt: seconds,
})
export type AttemptTimerRecord = z.infer<typeof attemptRecordSchema>

export type TimerPhase = "running" | "warning" | "overtime"

export interface TimerStatus {
  phase: TimerPhase
  remainingSeconds: number
  overtimeSeconds: number
}

const PREF_KEY_PREFIX = "studentTimer:v1:pref:"
const ATTEMPT_KEY_PREFIX = "studentTimer:v1:attempt:"
const ATTEMPT_RECORD_TTL_MS = 30 * 24 * 60 * 60 * 1000

function readJson(key: string): unknown {
  try {
    const raw = localStorage.getItem(key)
    return raw === null ? null : JSON.parse(raw)
  } catch {
    return null
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Best-effort: blocked or full storage must never break quiz taking.
  }
}

export function readTimerPreference(userId: string): StudentTimerPreference {
  const parsed = preferenceSchema.safeParse(
    readJson(`${PREF_KEY_PREFIX}${userId}`),
  )
  return parsed.success ? parsed.data : { ...DEFAULT_STUDENT_TIMER_PREFERENCE }
}

export function writeTimerPreference(
  userId: string,
  preference: StudentTimerPreference,
): void {
  writeJson(`${PREF_KEY_PREFIX}${userId}`, preference)
}

export function readAttemptTimerRecord(
  attemptId: string,
): AttemptTimerRecord | null {
  const parsed = attemptRecordSchema.safeParse(
    readJson(`${ATTEMPT_KEY_PREFIX}${attemptId}`),
  )
  return parsed.success ? parsed.data : null
}

function pruneStaleAttemptRecords(now: number): void {
  try {
    const staleKeys: string[] = []
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index)
      if (!key?.startsWith(ATTEMPT_KEY_PREFIX)) continue
      const parsed = attemptRecordSchema.safeParse(readJson(key))
      if (
        !parsed.success ||
        now - parsed.data.updatedAt > ATTEMPT_RECORD_TTL_MS
      ) {
        staleKeys.push(key)
      }
    }
    for (const key of staleKeys) localStorage.removeItem(key)
  } catch {
    // Best-effort cleanup.
  }
}

/**
 * Read-modify-write the attempt's record. Returns the new record even when
 * storage is unavailable so callers can keep working in memory. Stale records
 * are pruned only when a new attempt record is created, keeping the 1 Hz
 * overtime writes cheap.
 */
export function updateAttemptTimerRecord(
  attemptId: string,
  updater: (record: AttemptTimerRecord) => AttemptTimerRecord,
): AttemptTimerRecord {
  const now = Date.now()
  const existing = readAttemptTimerRecord(attemptId)
  if (!existing) pruneStaleAttemptRecords(now)
  const next = {
    ...updater(existing ?? { questionOvertime: {}, updatedAt: now }),
    updatedAt: now,
  }
  writeJson(`${ATTEMPT_KEY_PREFIX}${attemptId}`, next)
  return next
}

/** Warn in the last 20% of the limit, never less than 5s nor more than 5min. */
export function warningThresholdSeconds(limitSeconds: number): number {
  return Math.min(300, Math.max(5, Math.round(limitSeconds * 0.2)))
}

export function computeTimerStatus(
  limitSeconds: number,
  elapsedSeconds: number,
): TimerStatus {
  const elapsed = Math.max(0, Math.floor(elapsedSeconds))
  if (elapsed >= limitSeconds) {
    return {
      phase: "overtime",
      remainingSeconds: 0,
      overtimeSeconds: elapsed - limitSeconds,
    }
  }
  const remainingSeconds = limitSeconds - elapsed
  return {
    phase:
      remainingSeconds <= warningThresholdSeconds(limitSeconds)
        ? "warning"
        : "running",
    remainingSeconds,
    overtimeSeconds: 0,
  }
}

export function formatClock(totalSeconds: number): string {
  const total = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = (total % 60).toString().padStart(2, "0")
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs}`
  }
  return `${minutes}:${secs}`
}

/** Human duration for copy, e.g. 90 -> "1 min 30 s", 1800 -> "30 min". */
export function formatDurationLabel(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  if (minutes === 0) return `${secs} s`
  if (secs === 0) return `${minutes} min`
  return `${minutes} min ${secs} s`
}
